import React, { CSSProperties } from "react";
import { IonRow, IonCol, IonText, IonIcon, IonButton } from "@ionic/react";
import { trash } from "ionicons/icons";
import Select from "react-select/";
import { StablingAndExtrasItem, StablingAndExtrasOptionsData } from "../../../utilities/eventStallType/FormatStablingOptionsUtil";
import QuantitySelector from "../../Forms/QuantitySelector";
import { formatPriceForDisplayFromString } from "../../../utilities/currency/Currency";
import { FormattedRow } from "../../../utilities/stablingRequest/StablingAndExtrasUtil";

const groupBadgeStyles: CSSProperties = {
    backgroundColor: "var(--ion-color-white)",
    borderRadius: "2em",
    color: "var(--ion-color-dark)",
    display: "inline-block",
    fontSize: 12,
    fontWeight: "normal",
    lineHeight: "1",
    minWidth: 1,
    padding: "0.16666666666667em 0.5em",
    textAlign: "center",
};

interface StablingExtrasOrderEditorProps {
    rows: FormattedRow[];
    dropdownMenuOptions: StablingAndExtrasOptionsData[];
    onSelectItem: (item: StablingAndExtrasItem) => void;
    onQuantityChange: (rowIndex: number, quantity: number) => void;
    onRemoveItem: (rowIndex: number) => void;
    emptyMessage: string;
    selectInputId: string;
}

const StablingExtrasOrderEditor: React.FC<StablingExtrasOrderEditorProps> = ({
    rows,
    dropdownMenuOptions,
    onSelectItem,
    onQuantityChange,
    onRemoveItem,
    emptyMessage,
    selectInputId,
}) => {
    const calculateTotal = (orderRows: FormattedRow[]): number =>
        orderRows.reduce((total, row) => {
            const cost = parseFloat(row.item.cost) || 0;
            return total + row.quantity * cost;
        }, 0);

    const formatGroupLabel = (data: StablingAndExtrasOptionsData) => (
        <div>
            <IonRow>
                <IonCol size="10">
                    <span>{data.label}</span>
                </IonCol>
                <IonCol size="2">
                    <span style={groupBadgeStyles}>{data.options.length}</span>
                </IonCol>
            </IonRow>
        </div>
    );

    return (
        <>
            <IonRow className="px-2">
                <IonCol className="p-0">
                    <Select
                        id={selectInputId}
                        inputId={`${selectInputId}-input`}
                        controlShouldRenderValue={false}
                        placeholder="Select an item"
                        styles={{
                            control: (baseStyles) => ({ ...baseStyles }),
                            menu: (provided) => ({ ...provided, zIndex: 9999 }),
                            group: (base) => ({
                                ...base,
                                backgroundColor: "var(--ion-color-info)",
                                padding: "10px",
                            }),
                            groupHeading: (base) => ({
                                ...base,
                                fontSize: "1em",
                                fontWeight: "bold",
                                color: "var(--ion-color-white)",
                            }),
                            option: (base, { isFocused, isSelected }) => ({
                                ...base,
                                backgroundColor: isFocused || isSelected ? "var(--ion-color-light)" : "var(--ion-color-white)",
                                cursor: "pointer",
                                color: "var(--ion-color-primary)",
                            }),
                        }}
                        menuPortalTarget={document.body}
                        menuPlacement="auto"
                        options={dropdownMenuOptions}
                        noOptionsMessage={({ inputValue }) => (!inputValue ? "No options found." : "No results found.")}
                        formatGroupLabel={formatGroupLabel}
                        formatOptionLabel={(option: StablingAndExtrasItem) =>
                            option.label ? (
                                <div className="px-10" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
                                    <div style={{ flex: 1 }}>
                                        <div className="font-weight-bold text-default">{option.label}</div>
                                        {option.description && (
                                            <div className="display-5 text-medium" style={{ fontSize: "12px", marginTop: "4px" }}>
                                                {option.description}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ marginLeft: "16px", textAlign: "right", flexShrink: 0 }}>
                                        <div className="display-5 text-medium">{formatPriceForDisplayFromString(option.cost)}</div>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-0">
                                    <div className="font-weight-bold text-default">None Found ***</div>
                                </div>
                            )
                        }
                        onChange={(event: StablingAndExtrasItem | null) => {
                            if (event) onSelectItem(event);
                        }}
                    />
                </IonCol>
            </IonRow>

            {rows && rows.length > 0 ? (
                <div className="mt-2 mb-2 mx-2 rounded-3 overflow-hidden">
                    {rows.map((r, i) => {
                        const isFirstItem = i === 0;
                        const isLastItem = i === rows.length - 1;
                        const isOnlyItem = rows.length === 1;

                        let borderRadius = "0";
                        if (isOnlyItem) {
                            borderRadius = "8px";
                        } else if (isFirstItem) {
                            borderRadius = "8px 8px 0 0";
                        } else if (isLastItem) {
                            borderRadius = "0 0 8px 8px";
                        }

                        return (
                            <IonRow
                                key={i}
                                className="bg-white px-3"
                                style={{
                                    alignItems: "center",
                                    borderRadius: borderRadius,
                                    ...(i > 0 && { borderTop: "0.0625rem solid var(--ion-color-dark)" }),
                                }}>
                                <IonCol sizeXs="12" sizeSm="12" sizeMd="8" sizeLg="8" sizeXl="8" className="px-0" style={{ minWidth: 0 }}>
                                    <IonText className="text-dark" style={{ fontSize: "14px", display: "block", fontWeight: "bold" }}>
                                        {r.item.label}
                                    </IonText>
                                    {r.item.description && (
                                        <IonText className="text-dark" style={{ fontSize: "12px", display: "block", color: "#666", marginTop: "4px", lineHeight: "1.4" }}>
                                            {r.item.description}
                                        </IonText>
                                    )}
                                </IonCol>
                                <IonCol sizeXs="6" sizeSm="6" sizeMd="2" sizeLg="2" sizeXl="2" className="px-0 ion-text-center" style={{ flexShrink: 0 }}>
                                    {r.item.isRequired ? (
                                        <IonText className="text-dark px-0" style={{ fontSize: "14px", whiteSpace: "nowrap" }}>
                                            required
                                        </IonText>
                                    ) : (
                                        <QuantitySelector initialQuantity={r.quantity} onChange={(newQuantity) => onQuantityChange(i, newQuantity)} />
                                    )}
                                </IonCol>
                                <IonCol sizeXs="3" sizeSm="3" sizeMd="1" sizeLg="1" sizeXl="1" className="px-0 ion-text-center" style={{ flexShrink: 0 }}>
                                    <IonButton fill="clear" size="small" onClick={() => onRemoveItem(i)} style={{ margin: 0, padding: "4px", height: "auto" }}>
                                        {!r.item.isRequired && <IonIcon icon={trash} color="danger" style={{ fontSize: "20px" }} />}
                                    </IonButton>
                                </IonCol>
                                <IonCol sizeXs="3" sizeSm="3" sizeMd="1" sizeLg="1" sizeXl="1" className="px-0 ion-text-right" style={{ flexShrink: 0 }}>
                                    <IonText className="text-dark px-0" style={{ fontSize: "14px", whiteSpace: "nowrap" }}>
                                        {formatPriceForDisplayFromString(r.item.cost)}/ea
                                    </IonText>
                                </IonCol>
                            </IonRow>
                        );
                    })}
                    <IonRow className="mt-2 px-2 pb-0 border-top">
                        <IonCol>
                            <IonText className="text-primary display-5.5 font-weight-bold">Total</IonText>
                        </IonCol>
                        <IonCol className="ion-text-right">
                            <IonText className="text-primary display-5.5 font-weight-bold">
                                {formatPriceForDisplayFromString(calculateTotal(rows).toString())}
                            </IonText>
                        </IonCol>
                    </IonRow>
                </div>
            ) : (
                <p className="text-center text-danger px-2 pt-2" style={{ fontSize: "14px" }}>
                    {emptyMessage}
                </p>
            )}
        </>
    );
};

export default StablingExtrasOrderEditor;
